import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import styled from "styled-components";

import {
  listOrdenesCobroRemito,
  getOrdenCobroRemito,
} from "../helpers/rutaOrdenesCobroRemito";

import {
  listOrdenesPagoRemito,
  getOrdenPagoRemito,
} from "../helpers/rutaOrdenesPagoRemito";

import { generarPDFOrdenRemito } from "../helpers/pdfOrdenRemito";

import {
  dineroRemito,
  fechaRemitoVista,
  nombreEntidadRemito,
  estaAnuladaRemito,
  resumenOrdenRemito,
  numeroRemitoAplicado,
} from "../helpers/ordenesRemitoVista";

const CONFIG = {
  OC: {
    titulo: "Órdenes de cobro",
    persona: "cliente",
    etiqueta: "Cliente",
    vigente: "ACTIVA",
    anulada: "ANULADA",
    listar: listOrdenesCobroRemito,
    obtener: getOrdenCobroRemito,
  },
  OP: {
    titulo: "Órdenes de pago",
    persona: "proveedor",
    etiqueta: "Proveedor",
    vigente: "emitida",
    anulada: "anulada",
    listar: listOrdenesPagoRemito,
    obtener: getOrdenPagoRemito,
  },
};

const filtrosIniciales = {
  desde: "",
  hasta: "",
  estado: "",
};

const Styles = styled.div`
  color: #24324a;

  .ordenes-header {
    padding: 14px 16px;
    margin-bottom: 12px;
    border: 1px solid #e5dcc9;
    border-radius: 12px;
    background: linear-gradient(120deg, #fff, #f8f2e7);
  }

  .ordenes-header small {
    color: #987746;
    font-size: 10px;
    letter-spacing: 1.4px;
  }

  .ordenes-header h5 {
    margin: 4px 0 0;
    font-weight: 700;
  }

  .ordenes-filtros {
    display: flex;
    flex-wrap: wrap;
    align-items: end;
    gap: 10px;
    padding: 12px;
    margin-bottom: 12px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
  }

  .ordenes-filtros label {
    display: block;
    margin-bottom: 4px;
    font-size: 11px;
  }

  .ordenes-filtros .busqueda {
    flex: 1;
    min-width: 210px;
  }

  .ordenes-totales {
    display: flex;
    flex-wrap: wrap;
    gap: 12px 24px;
    padding: 12px;
    margin-bottom: 12px;
    border-radius: 8px;
    background: #edf4ff;
    color: #17488b;
    font-size: 12px;
  }

  .ordenes-table-wrap {
    overflow: auto;
    max-height: 460px;
    border: 1px solid #d7e0ea;
    border-radius: 8px;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
  }

  th,
  td {
    padding: 8px;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
    vertical-align: middle;
  }

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #1f5fd1;
    color: #fff;
  }

  tbody tr:nth-child(even) {
    background: #f7f9fc;
  }

  tbody tr:hover {
    background: #edf4ff;
  }

  .num {
    text-align: right;
  }

  .center {
    text-align: center;
  }

  .estado {
    display: inline-block;
    padding: 3px 7px;
    border-radius: 5px;
    font-size: 10px;
    font-weight: 700;
  }

  .vigente {
    color: #166534;
    background: #dcfce7;
  }

  .anulada {
    color: #991b1b;
    background: #fee2e2;
  }

  .pdf-btn {
    border: none;
    border-radius: 6px;
    background: transparent;
    color: #dc3545;
    width: 30px;
    height: 28px;
    cursor: pointer;
    font-size: 17px;
  }

  .pdf-btn:hover:not(:disabled) {
    background: #fee2e2;
  }

  .pdf-btn:disabled {
    opacity: 0.4;
    cursor: default;
  }

  .ordenes-paginacion {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 12px 0;
    font-size: 12px;
  }

  .ordenes-paginacion .botones {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const normalizarTexto = (valor) =>
  String(valor || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

export default function AppOrdenesRemitoReactTable({ tipo = "OC" }) {
  const c = CONFIG[tipo];

  const [ordenes, setOrdenes] = useState([]);
  const [filtros, setFiltros] = useState(filtrosIniciales);
  const [consulta, setConsulta] = useState(filtrosIniciales);
  const [busqueda, setBusqueda] = useState("");
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [pdfId, setPdfId] = useState(null);
  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(20);

  const solicitud = useRef(0);
  const generandoPDF = useRef(false);

  const cargar = useCallback(async () => {
    if (!c) return;

    const numeroSolicitud = ++solicitud.current;

    setCargando(true);
    setError("");

    try {
      const params = {};

      if (consulta.desde) params.desde = consulta.desde;
      if (consulta.hasta) params.hasta = consulta.hasta;
      if (consulta.estado) params.estado = consulta.estado;

      const respuesta = await c.listar(params);

      if (numeroSolicitud !== solicitud.current) return;

      if (!respuesta?.ok) {
        throw new Error(
          respuesta?.msg || "No se pudieron cargar las órdenes."
        );
      }

      if (!Array.isArray(respuesta.ordenes)) {
        throw new Error("La respuesta no contiene una lista de órdenes.");
      }

      setOrdenes(respuesta.ordenes);
      setPagina(0);
    } catch (e) {
      if (numeroSolicitud === solicitud.current) {
        setOrdenes([]);
        setError(e.message);
      }
    } finally {
      if (numeroSolicitud === solicitud.current) {
        setCargando(false);
      }
    }
  }, [c, consulta]);

  useEffect(() => {
    cargar();

    return () => {
      solicitud.current += 1;
    };
  }, [cargar]);

  const filtradas = useMemo(() => {
    if (!c) return [];

    const texto = normalizarTexto(busqueda).trim();

    if (!texto) return ordenes;

    return ordenes.filter((orden) => {
      const remitos = (orden.aplicaciones || [])
        .map(numeroRemitoAplicado)
        .join(" ");

      const contenido = [
        orden.numero,
        nombreEntidadRemito(orden[c.persona]),
        remitos,
      ].join(" ");

      return normalizarTexto(contenido).includes(texto);
    });
  }, [ordenes, busqueda, c]);

  const totales = useMemo(
    () =>
      filtradas.reduce(
        (acumulado, orden) => {
          if (estaAnuladaRemito(orden)) return acumulado;

          const r = resumenOrdenRemito(orden, tipo);

          acumulado.cantidad += 1;
          acumulado.aplicado += r.aplicado;
          acumulado.medios += r.medios;
          acumulado.retenciones += r.retenciones;
          acumulado.anticipos += r.anticipos;
          acumulado.generado += r.generado;

          return acumulado;
        },
        {
          cantidad: 0,
          aplicado: 0,
          medios: 0,
          retenciones: 0,
          anticipos: 0,
          generado: 0,
        }
      ),
    [filtradas, tipo]
  );

  const cantidadPaginas = Math.max(
    1,
    Math.ceil(filtradas.length / porPagina)
  );

  const paginaActual = Math.min(pagina, cantidadPaginas - 1);

  const visibles = filtradas.slice(
    paginaActual * porPagina,
    (paginaActual + 1) * porPagina
  );

  function aplicarFiltros() {
    if (
      filtros.desde &&
      filtros.hasta &&
      filtros.desde > filtros.hasta
    ) {
      setError("La fecha desde no puede ser posterior a la fecha hasta.");
      return;
    }

    setError("");
    setConsulta({ ...filtros });
  }

  function limpiar() {
    setFiltros({ ...filtrosIniciales });
    setConsulta({ ...filtrosIniciales });
    setBusqueda("");
    setPagina(0);
  }

  async function descargarPDF(orden) {
    if (!c || generandoPDF.current) return;

    generandoPDF.current = true;
    setPdfId(orden._id);
    setError("");

    try {
      // Obtiene la orden nuevamente con sus aplicaciones populadas.
      const respuesta = await c.obtener(orden._id);

      if (!respuesta?.ok || !respuesta?.orden?._id) {
        throw new Error(
          respuesta?.msg || "No se pudo obtener la orden para el PDF."
        );
      }

      generarPDFOrdenRemito(respuesta.orden, tipo);
    } catch (e) {
      setError(e.message);
    } finally {
      generandoPDF.current = false;
      setPdfId(null);
    }
  }

  if (!c) {
    return <Alert variant="danger">Tipo de orden inválido.</Alert>;
  }

  return (
    <Styles>
      <div className="ordenes-header">
        <small>INFORMES · PAPAS</small>
        <h5>{c.titulo}</h5>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="ordenes-filtros">
        <div>
          <label>Desde</label>
          <Form.Control
            size="sm"
            type="date"
            value={filtros.desde}
            onChange={(e) =>
              setFiltros((actual) => ({
                ...actual,
                desde: e.target.value,
              }))
            }
          />
        </div>

        <div>
          <label>Hasta</label>
          <Form.Control
            size="sm"
            type="date"
            value={filtros.hasta}
            onChange={(e) =>
              setFiltros((actual) => ({
                ...actual,
                hasta: e.target.value,
              }))
            }
          />
        </div>

        <div>
          <label>Estado</label>
          <Form.Select
            size="sm"
            value={filtros.estado}
            onChange={(e) =>
              setFiltros((actual) => ({
                ...actual,
                estado: e.target.value,
              }))
            }
          >
            <option value="">Todas</option>
            <option value={c.vigente}>Vigentes</option>
            <option value={c.anulada}>Anuladas</option>
          </Form.Select>
        </div>

        <div className="busqueda">
          <label>Número, {c.etiqueta.toLowerCase()} o remito</label>
          <Form.Control
            size="sm"
            value={busqueda}
            placeholder="Buscar…"
            onChange={(e) => {
              setBusqueda(e.target.value);
              setPagina(0);
            }}
          />
        </div>

        <Button
          size="sm"
          onClick={aplicarFiltros}
          disabled={cargando}
        >
          Consultar
        </Button>

        <Button
          size="sm"
          variant="outline-secondary"
          onClick={limpiar}
          disabled={cargando}
        >
          Limpiar
        </Button>

        <Button
          size="sm"
          variant="outline-dark"
          onClick={cargar}
          disabled={cargando}
        >
          Actualizar
        </Button>
      </div>

      <div className="ordenes-totales">
        <span>
          Vigentes: <strong>{totales.cantidad}</strong>
        </span>
        <span>
          Aplicado: <strong>{dineroRemito(totales.aplicado)}</strong>
        </span>
        <span>
          Medios: <strong>{dineroRemito(totales.medios)}</strong>
        </span>
        <span>
          Retenciones:{" "}
          <strong>{dineroRemito(totales.retenciones)}</strong>
        </span>
        <span>
          Anticipos utilizados:{" "}
          <strong>{dineroRemito(totales.anticipos)}</strong>
        </span>
        <span>
          Saldo a favor generado:{" "}
          <strong>{dineroRemito(totales.generado)}</strong>
        </span>
      </div>

      <div className="text-muted mb-2" style={{ fontSize: 11 }}>
        {filtradas.length} órdenes encontradas.
        Los totales corresponden a las órdenes vigentes de esta consulta.
      </div>

      <div className="ordenes-table-wrap">
        <table>
          <thead>
            <tr>
              <th>Número</th>
              <th>Fecha</th>
              <th>{c.etiqueta}</th>
              <th>Estado</th>
              <th>Remitos</th>
              <th className="num">Aplicado A</th>
              <th className="num">Aplicado B</th>
              {tipo === "OP" && <th className="num">Flete</th>}
              <th className="num">Total aplicado</th>
              <th className="num">Medios</th>
              <th className="num">Retenciones</th>
              <th className="num">Anticipos</th>
              <th className="num">Saldo a favor</th>
              <th className="center">PDF</th>
            </tr>
          </thead>

          <tbody>
            {cargando ? (
              <tr>
                <td colSpan={tipo === "OP" ? 14 : 13} className="center">
                  <span className="d-inline-flex align-items-center">
                    <Spinner animation="border" size="sm" />
                    <span style={{ marginLeft: 6 }}>Cargando…</span>
                  </span>
                </td>
              </tr>
            ) : visibles.length === 0 ? (
              <tr>
                <td colSpan={tipo === "OP" ? 14 : 13} className="center">
                  No hay órdenes para mostrar.
                </td>
              </tr>
            ) : (
              visibles.map((orden) => {
                const r = resumenOrdenRemito(orden, tipo);
                const anulada = estaAnuladaRemito(orden);

                const remitos = (orden.aplicaciones || [])
                  .map(numeroRemitoAplicado)
                  .join(", ");

                return (
                  <tr key={orden._id}>
                    <td>
                      <strong>{orden.numero || "-"}</strong>
                    </td>
                    <td>{fechaRemitoVista(orden.fecha)}</td>
                    <td>{nombreEntidadRemito(orden[c.persona])}</td>
                    <td>
                      <span
                        className={`estado ${
                          anulada ? "anulada" : "vigente"
                        }`}
                      >
                        {anulada ? "ANULADA" : "VIGENTE"}
                      </span>
                    </td>
                    <td>{remitos || "Anticipo"}</td>
                    <td className="num">{dineroRemito(r.aplicadoA)}</td>
                    <td className="num">{dineroRemito(r.aplicadoB)}</td>
                    {tipo === "OP" && (
                      <td className="num">
                        {dineroRemito(r.aplicadoFlete)}
                      </td>
                    )}
                    <td className="num">
                      <strong>{dineroRemito(r.aplicado)}</strong>
                    </td>
                    <td className="num">{dineroRemito(r.medios)}</td>
                    <td className="num">{dineroRemito(r.retenciones)}</td>
                    <td className="num">{dineroRemito(r.anticipos)}</td>
                    <td className="num">{dineroRemito(r.generado)}</td>
                    <td className="center">
                      <button
                        type="button"
                        className="pdf-btn"
                        title="Descargar PDF de la orden"
                        aria-label={`Descargar PDF ${orden.numero || ""}`}
                        disabled={Boolean(pdfId)}
                        onClick={() => descargarPDF(orden)}
                      >
                        {pdfId === orden._id ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <i
                            className="fa fa-file-pdf-o"
                            aria-hidden="true"
                          />
                        )}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="ordenes-paginacion">
        <Form.Select
          size="sm"
          style={{ width: 140 }}
          value={porPagina}
          onChange={(e) => {
            setPorPagina(Number(e.target.value));
            setPagina(0);
          }}
        >
          {[10, 20, 50, 100].map((cantidad) => (
            <option key={cantidad} value={cantidad}>
              Mostrar {cantidad}
            </option>
          ))}
        </Form.Select>

        <div className="botones">
          <Button
            size="sm"
            variant="outline-secondary"
            disabled={paginaActual === 0}
            onClick={() => setPagina(0)}
          >
            «
          </Button>

          <Button
            size="sm"
            variant="outline-secondary"
            disabled={paginaActual === 0}
            onClick={() => setPagina(paginaActual - 1)}
          >
            ‹
          </Button>

          <span>
            Página {paginaActual + 1} de {cantidadPaginas}
          </span>

          <Button
            size="sm"
            variant="outline-secondary"
            disabled={paginaActual >= cantidadPaginas - 1}
            onClick={() => setPagina(paginaActual + 1)}
          >
            ›
          </Button>

          <Button
            size="sm"
            variant="outline-secondary"
            disabled={paginaActual >= cantidadPaginas - 1}
            onClick={() => setPagina(cantidadPaginas - 1)}
          >
            »
          </Button>
        </div>
      </div>
    </Styles>
  );
}